import { useContext, useState } from "react";

// components
import { SidebarSiteSettings } from "./sidebar/sidebar-site-settings";
import { SearchLocation } from "./map-actions/search-location";
import { DrawProperty } from "./map-actions/draw-property";
import { SelectAccess } from "./map-actions/select-access";
import { DefineBorder } from "./map-actions/define-border";
import { MapToolbar } from "../map/map-toolbar";

// constants
import {
  DRAWMODE,
  PROPERTY_SOURCE,
  UNITS_DEFAULT,
} from "../../constants/site-settings.constants";

// style
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import { DisplayUploadedProperty } from "./map-actions/display-uploaded-property";
import { useSiteSettingsContext } from "../../services/context/site-settings-context";
import { useUserContext } from "../../services/context/user-context";
import { PropertyViewOnly } from "./map-actions/property-view-only";

export const SiteSettings = ({
  buildingArea,
  maxInnerBuildHeight,
  existedProperty,
}) => {
  const { viewOnly } = useUserContext();
  // Drawing states
  const [drawMode, setDrawMode] = useState(DRAWMODE.polygon);

  const {
    uploading,
    setUploading,
    deleteDrawing,
    setDeleteDrawing,
    hideGeoJSON,
    setHideGeoJSON,
  } = useSiteSettingsContext();

  // If user already has a property, start from step 3, otherwise from 0
  const [currentStep, setCurrentStep] = useState(existedProperty ? 3 : 0);
  const [stepComplete, setStepComplete] = useState(
    existedProperty
      ? {
          location: true,
          boundaries: true,
          access: true,
          regulations: true,
        }
      : {
          location: false,
          boundaries: false,
          access: false,
          regulations: false,
        }
  );
  const [isValidPolygon, setIsValidPolygon] = useState(true);

  const [propertyData, setPropertyData] = useState(
    existedProperty
      ? existedProperty
      : {
          location: null,
          locationGeodata: null,
          propertyGeoJSON: null,
          selectedLine: null,
          area: null,
          perimeter: null,
          minBorderDistance: 3,
          maxConstrHeight: null,
          buildingDensity: null,
          borderWidths: {},
          coordinatesCenter: { lat: null, long: null },
          maxBorder: null,
          propertyArea: null,
          propertySource: PROPERTY_SOURCE.HAND_DRAWN,
          fileId: null,
          fileName: null,
          units: UNITS_DEFAULT,
          /* pivot: null, */
          center: null,
          /* bearing: 0, */
          /* angle: 0, */
          /* uploadedFileId: null, */
          uploadedFileName: null,
          uploadedUnits: UNITS_DEFAULT,
          /* uploadedPivot: null,
          uploadedCenter: null, */
          uploadedPropertyGeoJSON: null,
          uploadedPropertyArea: null,
          uploadedPropertyPerimeter: null,
        }
  );

  const deletePropertyHandler = () => {
    setDeleteDrawing(true);
  };

  // update project data and step status
  const updatePropertyData = (key, value) => {
    if (key === "propertyGeoJSON") {
      setPropertyData((prevData) => ({
        ...prevData,
        [key]: value,
        // Reset access and regulations if new property is drawn
        selectedLine: null,
        minBorderDistance: 3,
        maxConstrHeight: null,
        buildingDensity: null,
        maxBorder: null,
      }));
    } else if (key === "locationGeodata") {
      setPropertyData((prevData) => ({
        ...prevData,
        [key]: value,
        location: null,
        propertyGeoJSON: null,
        selectedLine: null,
        area: null,
        perimeter: null,
        minBorderDistance: 3,
        maxConstrHeight: null,
        buildingDensity: null,
        borderWidths: {},
        coordinatesCenter: { lat: null, long: null },
        maxBorder: null,
        propertyArea: null,
        propertySource: PROPERTY_SOURCE.HAND_DRAWN,
        fileId: null,
        fileName: null,
        units: UNITS_DEFAULT,
        center: null,
        uploadedFileName: null,
        uploadedUnits: UNITS_DEFAULT,
        uploadedPropertyGeoJSON: null,
        uploadedPropertyArea: null,
        uploadedPropertyPerimeter: null,
      }));
    } else {
      setPropertyData((prevData) => ({
        ...prevData,
        [key]: value,
      }));
    }
    /* setPropertyData((prevData) => ({
      ...prevData,
      [key]: value,
    })); */

    if (key === "locationGeodata") {
      setStepComplete((prev) => ({
        ...prev,
        location: value !== null,
        boundaries: false,
        access: false,
        regulations: false,
      }));
    }

    if (key === "propertyGeoJSON") {
      setStepComplete(
        (prev) => ({
          ...prev,
          boundaries: value !== null,
          access: false,
          regulations: false,
        })
        /* prev.access || prev.regulations
          ? {
              ...prev,
              boundaries: value !== null,
              access: false,
              regulations: false,
            }
          : { ...prev, boundaries: value !== null } */
      );
    }
    if (key === "selectedLine") {
      setStepComplete((prev) => ({ ...prev, access: value !== null }));
    }

    if (
      key === "minBorderDistance" ||
      key === "maxConstrHeight" ||
      key === "buildingDensity"
    ) {
      const regulations = {
        minBorderDistance: propertyData.minBorderDistance,
        maxConstrHeight: propertyData.maxConstrHeight,
        /* buildingDensity: propertyData.buildingDensity, */
        [key]: value,
      };

      const notComplete = Object.values(regulations).some(
        (val) => val === null || val === ""
      );

      setStepComplete((prev) => ({ ...prev, regulations: !notComplete }));
    }
  };

  return (
    <>
      <SidebarSiteSettings
        propertyData={propertyData}
        currentStep={currentStep}
        steps={stepComplete}
        setSteps={setStepComplete}
        setCurrentStep={setCurrentStep}
        updatePropertyData={updatePropertyData}
        buildingArea={buildingArea}
        maxInnerBuildHeight={maxInnerBuildHeight}
        isValidPolygon={isValidPolygon}
        setUploading={setUploading}
        uploading={uploading}
        deleteDrawing={deleteDrawing}
      />
      <MapToolbar
        drawMode={drawMode}
        setDrawMode={setDrawMode}
        deletePropertyHandler={deletePropertyHandler}
        currentStep={currentStep}
        isSiteSettings={true}
        isFileSource={
          uploading || propertyData.propertySource === PROPERTY_SOURCE.FILE
        }
        property={propertyData ?? null}
        propertyLocation={propertyData.coordinatesCenter}
      />
      {currentStep === 0 ? (
        <SearchLocation
          updatePropertyData={updatePropertyData}
          searchedResult={propertyData.locationGeodata}
        />
      ) : currentStep === 1 ? (
        viewOnly ? (
          <PropertyViewOnly
            propertyGeoJSON={propertyData.propertyGeoJSON}
          />
        ) : (uploading &&
            !hideGeoJSON &&
            propertyData.uploadedPropertyGeoJSON) ||
          (!uploading &&
            propertyData.propertySource === PROPERTY_SOURCE.FILE &&
            propertyData.propertyGeoJSON) ? (
          <DisplayUploadedProperty
            propertyGeoJSON={
              uploading
                ? propertyData.uploadedPropertyGeoJSON
                : propertyData.propertyGeoJSON
            }
            updatePropertyData={updatePropertyData}
            deleteDrawing={deleteDrawing}
            setDeleteDrawing={setDeleteDrawing}
            uploading={uploading}
            /* bearing={propertyData.bearing} */
          />
        ) : uploading &&
          (!propertyData.uploadedPropertyGeoJSON || hideGeoJSON) ? null : (
          <DrawProperty
            propertyGeoJSON={propertyData.propertyGeoJSON}
            updatePropertyData={updatePropertyData}
            drawMode={drawMode}
            setDrawMode={setDrawMode}
            deleteDrawing={deleteDrawing}
            setDeleteDrawing={setDeleteDrawing}
            setIsValidPolygon={setIsValidPolygon}
            uploadedPropertyGeoJSON={propertyData.uploadedPropertyGeoJSON}
          />
        )
      ) : currentStep === 2 ? (
        <SelectAccess
          updatePropertyData={updatePropertyData}
          propertyGeoJSON={propertyData.propertyGeoJSON}
          selectedLine={propertyData.selectedLine}
        />
      ) : (
        <DefineBorder
          propertyData={propertyData}
          updatePropertyData={updatePropertyData}
        />
      )}
    </>
  );
};
